import React, { useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { HealthBadge, SectionTitle, Empty, Spinner, StatCard } from "@/components/common";
import PmHub from "@/components/pm/PmHub";
import IntegrationsPanel from "@/components/pm/IntegrationsPanel";
import { toast } from "sonner";
import { Plus, CalendarClock, FileText, Settings, ArrowRight, Network, Boxes, Users, Download, Eye, ListChecks, Share2 } from "lucide-react";

export default function ProjectHub() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "overview";
  const [open, setOpen] = useState(false);
  const [mtitle, setMtitle] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState("");

  const projectQ = useQuery({ queryKey: ["project", pid], queryFn: async () => (await api.get(`/projects/${pid}`)).data });
  const meetingsQ = useQuery({ queryKey: ["meetings", pid], queryFn: async () => (await api.get(`/projects/${pid}/meetings`)).data });
  const docsQ = useQuery({ queryKey: ["documents", pid], queryFn: async () => (await api.get(`/projects/${pid}/documents`)).data });

  const p = projectQ.data;
  if (projectQ.isLoading) return <Spinner label="Loading project…" />;
  if (!p) return null;

  const portalShare = async () => {
    const res = await api.post(`/projects/${pid}/portal-share`);
    const url = `${window.location.origin}/share/${res.data.token}`;
    navigator.clipboard.writeText(url);
    toast.success("Stakeholder portal link copied to clipboard");
  };

  const createMeeting = async () => {    if (!mtitle) { toast.error("Enter a meeting title"); return; }
    const res = await api.post(`/projects/${pid}/meetings`, { title: mtitle, agenda: [], attendees: [] });
    setOpen(false); setMtitle("");
    navigate(`/projects/${pid}/meetings/${res.data.id}`);
  };

  const shareWithTeammate = async () => {
    if (!shareEmail.includes("@")) { toast.error("Enter a valid email"); return; }
    try {
      const r = await api.post(`/projects/${pid}/share`, { email: shareEmail });
      setShareOpen(false); setShareEmail("");
      if (r.data.status === "added") toast.success(`Shared with ${r.data.email}`);
      else if (r.data.email_sent) toast.success(`Invitation emailed to ${r.data.email}`);
      else { navigator.clipboard?.writeText(r.data.link); toast.success("Invite link copied (email not configured)"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not share"); }
  };

  const sys = p.systems || {};

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="rounded-2xl brand-gradient text-white p-7 relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-60 h-60 rounded-full bg-[#29abe2]/20 blur-3xl" />
        <div className="relative z-10 flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-[11px] font-semibold tracking-[0.2em] text-[#c9a227] uppercase">{p.client_name} · {p.industry}</div>
            <h1 className="text-3xl font-bold tracking-tight mt-1">{p.name}</h1>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {sys.sap_product && <span className="text-xs bg-white/10 px-2.5 py-1 rounded-full">{sys.sap_product}</span>}
              {(sys.sap_modules || []).slice(0, 6).map((m) => <span key={m} className="text-xs bg-white/10 px-2.5 py-1 rounded-full">{m}</span>)}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <HealthBadge status={p.health} />
            <Dialog open={shareOpen} onOpenChange={setShareOpen}>
              <DialogTrigger asChild><Button variant="outline" className="gap-2 bg-white/10 border-white/20 text-white hover:bg-white/20" data-testid="invite-teammate-btn"><Users size={15} /> Invite teammate</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Invite a teammate</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <Label className="text-xs">Teammate email</Label>
                  <Input placeholder="colleague@company.com" value={shareEmail} onChange={(e) => setShareEmail(e.target.value)} data-testid="share-email" />
                  <p className="text-xs text-muted-foreground">They get access to this project. If they don't have an account yet, a branded one-click join invite is emailed to them.</p>
                </div>
                <DialogFooter><Button onClick={shareWithTeammate} data-testid="send-share-btn">Share project</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <Button variant="outline" onClick={portalShare} className="gap-2 bg-white/10 border-white/20 text-white hover:bg-white/20" data-testid="portal-share-btn"><Share2 size={15} /> Stakeholder Portal</Button>
            <Button variant="outline" onClick={() => navigate(`/projects/${pid}/edit`)} className="gap-2 bg-white/10 border-white/20 text-white hover:bg-white/20"><Settings size={15} /> Edit</Button>
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => setSp({ tab: v })}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="meetings" data-testid="tab-meetings">Meetings</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations">Integrations</TabsTrigger>
          <TabsTrigger value="plan" data-testid="tab-plan">Project Plan</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="mt-5 space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={CalendarClock} label="Meetings" value={meetingsQ.data?.length || 0} accent="cyan" />
            <StatCard icon={FileText} label="Documents" value={docsQ.data?.length || 0} accent="cyan" />
            <StatCard icon={Boxes} label="SAP Modules" value={(sys.sap_modules || []).length} accent="navy" />
            <StatCard icon={Network} label="CRM / Systems" value={(sys.crm_systems || []).length + (sys.other_systems || []).length} accent="gold" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-xl border bg-card p-5">
              <div className="flex items-center gap-2 font-semibold text-[#0f2557] dark:text-[#29abe2] mb-3"><Network size={16} /> Landscape</div>
              <dl className="text-sm space-y-2">
                {[["SAP Product", sys.sap_product], ["Deployment", sys.deployment], ["Middleware", sys.middleware], ["Version", sys.version]].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4"><dt className="text-muted-foreground">{k}</dt><dd className="text-foreground font-medium text-right">{v || "—"}</dd></div>
                ))}
              </dl>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {[...(sys.crm_systems || []), ...(sys.other_systems || [])].map((s) => <span key={s} className="text-[10px] bg-secondary px-2 py-0.5 rounded">{s}</span>)}
              </div>
            </div>
            <div className="rounded-xl border bg-card p-5">
              <div className="flex items-center gap-2 font-semibold text-[#0f2557] dark:text-[#29abe2] mb-3"><Users size={16} /> Team & Stakeholders</div>
              <div className="space-y-1.5 text-sm">
                {[...(p.team || []), ...(p.stakeholders || [])].map((m, i) => (
                  <div key={i} className="flex justify-between"><span className="text-foreground">{m.name || "—"}</span><span className="text-muted-foreground text-xs">{m.role} {m.org && `· ${m.org}`}</span></div>
                ))}
                {![...(p.team || []), ...(p.stakeholders || [])].length && <p className="text-muted-foreground text-xs">No people added.</p>}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Meetings */}
        <TabsContent value="meetings" className="mt-5">
          <SectionTitle title="Meetings" action={
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button className="gap-2" data-testid="new-meeting-btn"><Plus size={15} /> New meeting</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>New meeting</DialogTitle></DialogHeader>
                <div><Label>Meeting title</Label><Input autoFocus value={mtitle} onChange={(e) => setMtitle(e.target.value)} placeholder="Order-to-Cash discovery workshop" data-testid="new-meeting-title" /></div>
                <DialogFooter><Button onClick={createMeeting} data-testid="create-meeting">Create & plan</Button></DialogFooter>
              </DialogContent>
            </Dialog>} />
          {meetingsQ.isLoading ? <Spinner /> : meetingsQ.data?.length === 0 ? (
            <Empty title="No meetings yet" hint="Create a meeting to plan the agenda, run the live room and generate deliverables." />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {meetingsQ.data.map((m) => (
                <button key={m.id} onClick={() => navigate(`/projects/${pid}/meetings/${m.id}`)} data-testid={`meeting-${m.id}`}
                  className="text-left rounded-xl border bg-card p-5 card-hover">
                  <div className="flex items-center justify-between">
                    <span className="grid place-items-center w-10 h-10 rounded-lg bg-[#29abe2]/15 text-[#1a7bb0] dark:text-[#5fc3f0]"><CalendarClock size={18} /></span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground capitalize">{m.status}</span>
                  </div>
                  <div className="font-semibold text-foreground mt-3">{m.title}</div>
                  <div className="text-xs text-muted-foreground">{m.agenda?.length || 0} agenda items · {m.attendees?.length || 0} attendees</div>
                  <div className="flex items-center gap-1 text-xs text-[#29abe2] mt-3">Open workspace <ArrowRight size={13} /></div>
                </button>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Documents */}
        <TabsContent value="documents" className="mt-5">
          <SectionTitle title="Document Pack" />
          {docsQ.isLoading ? <Spinner /> : docsQ.data?.length === 0 ? (
            <Empty title="No documents yet" hint="Generate deliverables from a meeting's Generate step." />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {docsQ.data.map((d) => (
                <div key={d.id} className="rounded-xl border bg-card p-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-[#0f2557] dark:text-[#29abe2]">{d.doc_type}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${d.status === "approved" ? "bg-emerald-500/15 text-emerald-600" : "bg-secondary text-muted-foreground"}`}>{d.status} v{d.version}</span>
                  </div>
                  <div className="font-medium text-sm text-foreground mt-1 truncate">{d.title}</div>
                  <div className="text-[11px] text-muted-foreground font-mono">{d.reference}</div>
                  <div className="flex gap-2 mt-3 flex-wrap">
                    <a href={`${API}/documents/${d.id}/export?format=html`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="h-8 gap-1.5" data-testid={`ph-view-${d.doc_type}`}><Eye size={13} /> View</Button></a>
                    <a href={`${API}/documents/${d.id}/export?format=pdf`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="h-8 gap-1.5" data-testid={`ph-pdf-${d.doc_type}`}><Download size={13} /> PDF</Button></a>
                    <a href={`${API}/documents/${d.id}/export?format=docx`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="h-8 gap-1.5" data-testid={`ph-docx-${d.doc_type}`}><Download size={13} /> DOCX</Button></a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="integrations" className="mt-5">
          <IntegrationsPanel pid={pid} meetings={meetingsQ.data || []} />
        </TabsContent>

        <TabsContent value="plan" className="mt-5">
          <PmHub pid={pid} meetings={meetingsQ.data || []} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
